import React, { Suspense, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'
import { useModal } from '@gluedigital/modal'
import Header from 'src/components/Header/Header'
import Loading from 'src/components/Loading/Loading'
import MapAssets from 'src/components/MapAssets/MapAssets'
import { useAsset } from 'src/hooks/api'
import { useGeolocation, isNearby } from 'src/hooks/geo'
import usePendingArmStatus from './usePendingArmStatus'
import { correctPhone } from 'src/util/mix'
import direction from 'src/static/vectors/direction.svg'
import direction2 from 'src/static/vectors/direction-mob.svg'
import { AssetDetails, Partition } from 'src/types'

import './PartitionPicker'
import './Asset.sass'

const Asset = () => {
  const { assetId } = useParams<{ assetId: string }>()
  const asset = useAsset(assetId)
  const assets = useMemo(() => [asset.active], [asset])

  const [subscriberCode, setSubscriberCode] = useState(asset.active.subscribers?.[0]?.code)

  return (
    <div id="asset" className="page">
      <Header back />
      <section id="page-content">
        <header className="map-header">
          <MapAssets assets={assets} center="assets" />
        </header>
        <div className="target-picker">
          <span>Abonado:</span>
          <select value={subscriberCode} onChange={e => setSubscriberCode(e.target.value)}>
            {asset.active.subscribers.map(sub =>
              <option value={sub.code}>
                {sub.label || sub.code}
              </option>
            )}
          </select>
        </div>
        <AssetSubscriber asset={asset} subscriberCode={subscriberCode} key={subscriberCode} />
      </section>
    </div>
  )
}

interface AssetSubscriberProps {
  asset: AssetDetails
  subscriberCode: string
}

const AssetSubscriber = ({ asset, subscriberCode }: AssetSubscriberProps) => {
  const loc = useGeolocation(true)
  const modal = useModal()

  const { active, canArmRemotely, editableInfo } = asset
  const subscriber = active.subscribers?.find(x => x.code === subscriberCode)
  const { comment, description } = editableInfo

  const {
    lat,
    lon,
    address,
    city,
    province,
    zipCode,
    prinexId,
  } = active

  const hasPartitions = subscriber?.partitions?.length > 0
  const partitions: Partition[] = subscriber?.partitions?.map((p, i) => ({ ...p, idx: i }))
  const [selectedPartitionIds, setSelectedPartitionIds] = useState<string[]>([])
  const selectedPartitions = selectedPartitionIds.map(id => partitions.find(p => p.id === id))
  let selectedPartitionsLabel = 'Todas'
  if (selectedPartitions.length === 1) {
    selectedPartitionsLabel = selectedPartitions[0].name || ('Partición ' + selectedPartitions[0].idx)
  } else if (selectedPartitions.length > 1) {
    selectedPartitionsLabel = selectedPartitions.length + ' particiones'
  }
  const handlePartition = () => {
    modal.show('partition-picker', { partitions, selectedPartitionIds, setSelectedPartitionIds })
  }

  const {
    armed,
    isLoading,
    isError,
    status,
    toggle
  } = usePendingArmStatus(asset, subscriber, selectedPartitions)

  const geoBlocked = !canArmRemotely && !isNearby(active, loc)
  const canArm = !isLoading && !geoBlocked

  const directionsLink = 'https://www.google.com/maps/dir/?api=1' +
    '&destination=' + lat + ',' + lon

  return (
    <>
      {hasPartitions &&
        <div className="partition-picker">
          <span>Particiones:</span>
          <strong onClick={handlePartition}>
            {selectedPartitionsLabel}
          </strong>
        </div>
      }
      <div className={`armed-badge ${isLoading ? 'pending' : armed}`}>
        <span>Estado:</span>
        <strong><FormattedMessage id={`asset.${isLoading ? 'pending' : armed}`} /></strong>
      </div>
      <div className="container">
        <button onClick={() => toggle(subscriberCode, loc)} disabled={!canArm}>
          {!isLoading &&
            <strong><FormattedMessage id={`asset.button-${armed}`} /></strong>
          }
          {isLoading &&
            <Loading />
          }
        </button>
        {geoBlocked &&
          <p className="error">
            <FormattedMessage id={loc ? 'asset.geoblocked' : 'asset.no-geo'} />
          </p>
        }
        {isError &&
          <>
            <p className="error">
              <FormattedMessage id="asset.arm-error" />
              <FormattedMessage id={'asset.arm-error.' + status} />
            </p>
            <p className="error">
              <FormattedMessage
                id="asset.arm-error-phone"
                values={{ phone: correctPhone(asset.active?.customerId ?? '') }}
              />
            </p>
          </>
        }
        <h3>
          {`${address}, ${city}; CP: (${zipCode}), ${province}`}
        </h3>
        <div>
          <p className="data">
            <strong>Nº Activo: </strong>
            <span>{prinexId || '-'}</span>
          </p>
        </div>
        <a className="button filled direction-link" href={directionsLink} target="_blank" rel="noreferrer">
          <img className="only-desktop" src={direction} alt="icon direction" />
          <img className="only-mobile" src={direction2} alt="icon direction" />
          <span>Cómo llegar</span>
        </a>
        <div className="split">
          <div className="item item-left">
            <h4>Indicaciones</h4>
            <p>{comment || '-'}</p>
          </div>
          <div className="item item-right">
            <h4>Descripción</h4>
            <p>{description || '-'}</p>
          </div>
        </div>
      </div>
    </>
  )
}

const AssetWrapper = () =>
  <Suspense fallback={<Loading />}>
    <Asset />
  </Suspense>

export default AssetWrapper
